import { api } from './api';

/**
 * Per-user notification preferences.
 * Aligned to app/api/v1/routers/notification_settings_router.py
 * (prefix /api/v1/notification-settings). Every call is scoped to the
 * authenticated user by the backend — no id is passed for identity.
 *
 * `notificationId` = a notification-master _id, or "all" for the global default.
 */
export interface QuietHours {
  start?: string;      // "22:00"
  end?: string;        // "07:00"
  timezone?: string;   // "Asia/Kolkata"
}

export interface NotificationSetting {
  id: string;
  notificationId: string | null;   // null = global default row
  optedOut?: boolean;
  channels?: string[] | null;      // null = inherit master defaults
  maxRepeats?: number | null;
  repeatCooldownMinutes?: number | null;
  quietHours?: QuietHours | null;
}

export interface NotificationSettingUpsert {
  optedOut?: boolean;
  channels?: string[] | null;
  maxRepeats?: number | null;
  repeatCooldownMinutes?: number | null;
  quietHours?: QuietHours | null;
}

const BASE = '/api/v1/notification-settings';
const GLOBAL_KEY = 'all';

export const notificationSettingsService = {
  /** List my preferences (global row + any per-type overrides). */
  listMine: async (): Promise<NotificationSetting[]> => {
    const res = await api.get<{ data: NotificationSetting[]; total: number }>(`${BASE}/me`);
    return (res.data as any)?.data ?? [];
  },

  /** Upsert one preference. Pass 'all' for the global default. */
  upsert: async (
    notificationId: string | null,
    patch: NotificationSettingUpsert,
  ): Promise<NotificationSetting> => {
    const key = notificationId || GLOBAL_KEY;
    const res = await api.put<NotificationSetting>(`${BASE}/me/${key}`, patch);
    return res.data as NotificationSetting;
  },

  /** Remove one preference (reverts to inherited/default behaviour). */
  remove: async (notificationId: string | null): Promise<boolean> => {
    const key = notificationId || GLOBAL_KEY;
    const res = await api.delete<{ removed: boolean }>(`${BASE}/me/${key}`);
    return !!(res.data as any)?.removed;
  },
};

export default notificationSettingsService;
